import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Paper,
  IconButton,
  Skeleton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Drawer,
  Switch,
  FormControlLabel,
} from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import ViewWeekIcon from '@mui/icons-material/ViewWeek'
import ViewDayIcon from '@mui/icons-material/ViewDay'
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import BusinessIcon from '@mui/icons-material/Business'
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import TimelineIcon from '@mui/icons-material/Timeline'
import LoginIcon from '@mui/icons-material/Login'
import LogoutIcon from '@mui/icons-material/Logout'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CloseIcon from '@mui/icons-material/Close'
import EventIcon from '@mui/icons-material/Event'
import RefreshIcon from '@mui/icons-material/Refresh'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import VisibilityIcon from '@mui/icons-material/Visibility'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd'
import PendingActionsIcon from '@mui/icons-material/PendingActions'
import dayjs from 'dayjs'
import { getMyRoster, getMyEmrUsageLogs, extractRosterSlots, extractUsageLogs } from './mentorRosterService'

const PRESENCE_LOG_EVENT_IDS = ['log-check-in', 'log-check-out', 'log-thrown-out']

const getLogEventInfo = (logEventId) => {
  const map = {
    'log-check-in': { title: 'Check-In', icon: 'login', color: '#10b981' },
    'log-check-in-refresh': { title: 'Check-In Refresh', icon: 'refresh', color: '#0ea5e9' },
    'log-check-out': { title: 'Check-Out', icon: 'logout', color: '#f59e0b' },
    'log-thrown-out': { title: 'Thrown Out', icon: 'warning', color: '#ef4444' },
    'log-check-in-request': { title: 'Check-In Request', icon: 'request', color: '#8b5cf6' },
    'log-check-in-request-ack': { title: 'Request Approved', icon: 'approved', color: '#10b981' },
    'log-check-in-request-denied': { title: 'Request Denied', icon: 'denied', color: '#ef4444' },
    're-check-in': { title: 'Re-Check-In', icon: 'login', color: '#0891b2' },
    'log-screen-idle': { title: 'Screen Idle', icon: 'idle', color: '#94a3b8' },
    'enter-without-checkin': { title: 'Enter Without Check-In', icon: 'peek', color: '#f97316' },
    'log-peek-in-refresh': { title: 'Peek-In Refresh', icon: 'refresh', color: '#64748b' },
  }
  return map[logEventId] || { title: logEventId, icon: 'default', color: '#64748b' }
}

const minsBetween = (startTime, endTime) => {
  if (!startTime || !endTime) return 0
  const [sh, sm] = startTime.split(':').map(Number)
  const [eh, em] = endTime.split(':').map(Number)
  const start = sh * 60 + sm
  const end = eh * 60 + em
  return end >= start ? end - start : 0
}

const fmtDuration = (mins) => {
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return m > 0 ? `${h}h ${m}m` : `${h}h`
}

/**
 * Practitioner's own roster, rendered inline inside the mentor clinic bookings dashboard.
 * Modeled on the support PractitionerRosterView but self-scoped (own profile, no force-checkout) and
 * with a "This clinic / All clinics" scope toggle so the practitioner can step out of the current
 * clinic context without leaving the dashboard.
 */
export default function MentorRosterView({ mentorProfileData, clinicId, clinicBrandId, clinicName }) {
  const [scope, setScope] = useState('this') // 'this' = current clinic location, 'all' = every clinic
  const [viewType, setViewType] = useState('weekly')
  const [selectedDate, setSelectedDate] = useState(dayjs())
  const [selectedWeek, setSelectedWeek] = useState(dayjs().startOf('week'))
  const [selectedMonth, setSelectedMonth] = useState(dayjs().startOf('month'))
  const [loading, setLoading] = useState(true)
  const [rosterData, setRosterData] = useState([])
  const [emrLogsData, setEmrLogsData] = useState([])
  const [timelineDialogOpen, setTimelineDialogOpen] = useState(false)
  const [timelineDate, setTimelineDate] = useState(null)
  const [showAllLogs, setShowAllLogs] = useState(false)
  const [allEmrLogsData, setAllEmrLogsData] = useState([])

  const clinicLocationIds = scope === 'this' && clinicId ? [clinicId] : undefined

  const range = useMemo(() => {
    if (viewType === 'daily') {
      return { fromDate: selectedDate.format('YYYY-MM-DD'), toDate: selectedDate.format('YYYY-MM-DD') }
    }
    if (viewType === 'weekly') {
      return {
        fromDate: selectedWeek.startOf('week').format('YYYY-MM-DD'),
        toDate: selectedWeek.endOf('week').format('YYYY-MM-DD'),
      }
    }
    return {
      fromDate: selectedMonth.startOf('month').format('YYYY-MM-DD'),
      toDate: selectedMonth.endOf('month').format('YYYY-MM-DD'),
    }
  }, [viewType, selectedDate, selectedWeek, selectedMonth])

  // Fetch roster slots
  useEffect(() => {
    let cancelled = false
    const run = async () => {
      setLoading(true)
      try {
        const resp = await getMyRoster({
          fromDate: range.fromDate,
          toDate: range.toDate,
          clinicLocationIds,
          includeBookedSlots: true,
        })
        if (!cancelled) {
          setRosterData(resp?.status === 200 ? extractRosterSlots(resp) : [])
        }
      } catch (e) {
        if (!cancelled) setRosterData([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [range.fromDate, range.toDate, scope, clinicId])

  // Fetch presence (EMR usage) logs
  useEffect(() => {
    let cancelled = false
    const run = async () => {
      try {
        const resp = await getMyEmrUsageLogs({
          fromDate: range.fromDate,
          toDate: range.toDate,
          clinicLocationIds,
          logEventIds: PRESENCE_LOG_EVENT_IDS,
        })
        if (!cancelled) {
          setEmrLogsData(resp?.status === 200 ? extractUsageLogs(resp) : [])
        }
      } catch (e) {
        if (!cancelled) setEmrLogsData([])
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [range.fromDate, range.toDate, scope, clinicId])

  // All log types for the open timeline date (only when toggled on)
  useEffect(() => {
    let cancelled = false
    const run = async () => {
      if (!timelineDate || !showAllLogs) {
        setAllEmrLogsData([])
        return
      }
      try {
        const dateStr = timelineDate.format('YYYY-MM-DD')
        const resp = await getMyEmrUsageLogs({ fromDate: dateStr, toDate: dateStr, clinicLocationIds })
        if (!cancelled) setAllEmrLogsData(resp?.status === 200 ? extractUsageLogs(resp) : [])
      } catch (e) {
        if (!cancelled) setAllEmrLogsData([])
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [timelineDate, showAllLogs, scope, clinicId])

  const weekDays = useMemo(() => {
    const days = []
    for (let i = 0; i < 7; i++) days.push(selectedWeek.startOf('week').add(i, 'day'))
    return days
  }, [selectedWeek])

  const monthCalendar = useMemo(() => {
    const start = selectedMonth.startOf('month').startOf('week')
    const end = selectedMonth.endOf('month').endOf('week')
    const weeks = []
    let current = start
    while (current.isBefore(end) || current.isSame(end, 'day')) {
      const week = []
      for (let i = 0; i < 7; i++) {
        week.push(current)
        current = current.add(1, 'day')
      }
      weeks.push(week)
    }
    return weeks
  }, [selectedMonth])

  const slotsByDate = useMemo(() => {
    const grouped = new Map()
    rosterData.forEach((slot) => {
      const date = slot.customDate || slot.slotDate || slot.date
      if (!date) return
      const existing = grouped.get(date) || []
      existing.push(slot)
      grouped.set(date, existing)
    })
    grouped.forEach((slots) => {
      slots.sort((a, b) =>
        (a.slotStartTime || a.startTime || '00:00').localeCompare(b.slotStartTime || b.startTime || '00:00'),
      )
    })
    return grouped
  }, [rosterData])

  const totalAllottedMinutes = useMemo(
    () =>
      rosterData.reduce(
        (sum, s) => sum + minsBetween(s.slotStartTime || s.startTime, s.slotEndTime || s.endTime),
        0,
      ),
    [rosterData],
  )

  const emrLogsByDate = useMemo(() => {
    const grouped = new Map()
    emrLogsData.forEach((log) => {
      const date = log.logDate
      if (!date) return
      const existing = grouped.get(date) || []
      existing.push(log)
      grouped.set(date, existing)
    })
    grouped.forEach((logs) => logs.sort((a, b) => (a.logTime || '').localeCompare(b.logTime || '')))
    return grouped
  }, [emrLogsData])

  const rosterMinutesByDate = useMemo(() => {
    const byDate = new Map()
    rosterData.forEach((slot) => {
      const date = slot.customDate || slot.slotDate || slot.date
      const mins = minsBetween(slot.slotStartTime || slot.startTime, slot.slotEndTime || slot.endTime)
      if (date) byDate.set(date, (byDate.get(date) || 0) + mins)
    })
    return byDate
  }, [rosterData])

  // Pair check-ins with check-outs → actual presence
  const actualPresenceData = useMemo(() => {
    const presenceByDate = new Map()
    let totalActualMinutes = 0
    const incompleteCheckIns = []

    emrLogsByDate.forEach((logs, date) => {
      const checkIns = logs.filter((l) => l.logEventId === 'log-check-in')
      const checkOuts = logs.filter(
        (l) => l.logEventId === 'log-check-out' || l.logEventId === 'log-thrown-out',
      )
      const pairings = []
      const usedIdx = new Set()
      checkIns.forEach((checkIn) => {
        let match = null
        let matchIdx = -1
        for (let i = 0; i < checkOuts.length; i++) {
          if (usedIdx.has(i)) continue
          const co = checkOuts[i]
          if (co.logTime > checkIn.logTime) {
            if (co.chamberRoomId === checkIn.chamberRoomId) {
              match = co
              matchIdx = i
              break
            }
            if (!match) {
              match = co
              matchIdx = i
            }
          }
        }
        if (match) {
          usedIdx.add(matchIdx)
          const mins = minsBetween(checkIn.logTime, match.logTime)
          totalActualMinutes += mins
          pairings.push({ checkIn, checkOut: match, durationMins: mins })
        } else {
          incompleteCheckIns.push({ ...checkIn, date })
        }
      })
      if (pairings.length > 0) presenceByDate.set(date, pairings)
    })

    return { presenceByDate, totalActualMinutes, incompleteCheckIns }
  }, [emrLogsByDate])

  const overallPercentage =
    totalAllottedMinutes === 0
      ? 0
      : Math.round((actualPresenceData.totalActualMinutes / totalAllottedMinutes) * 100)

  const handleOpenTimeline = (date) => {
    setTimelineDate(date)
    setTimelineDialogOpen(true)
  }

  const getTimelineEvents = (date) => {
    const dateStr = dayjs(date).format('YYYY-MM-DD')
    const events = []
    ;(slotsByDate.get(dateStr) || []).forEach((slot) => {
      events.push({
        time: slot.slotStartTime,
        title: 'Roster Start',
        subtitle: `${slot.chamberRoomTitle || 'Room'} @ ${slot.clinicBrandName || 'Clinic'}`,
        icon: 'event',
        color: '#6366f1',
      })
      events.push({
        time: slot.slotEndTime,
        title: 'Roster End',
        subtitle: `${slot.chamberRoomTitle || 'Room'}`,
        icon: 'event',
        color: '#6366f1',
      })
    })
    const logs = showAllLogs
      ? allEmrLogsData
          .filter((l) => l.logDate === dateStr)
          .sort((a, b) => (a.logTime || '').localeCompare(b.logTime || ''))
      : emrLogsByDate.get(dateStr) || []
    logs.forEach((log) => {
      const info = getLogEventInfo(log.logEventId)
      events.push({
        time: log.logTime?.substring(0, 5) || '--:--',
        title: info.title,
        subtitle: `${log.chamberRoomTitle || 'Room'} @ ${log.clinicBrandName || 'Clinic'}`,
        icon: info.icon,
        color: info.color,
        checkinMethod: log.checkinMethod,
      })
    })
    events.sort((a, b) => (a.time || '').localeCompare(b.time || ''))
    return events
  }

  const handlePrevious = () => {
    if (viewType === 'daily') setSelectedDate(selectedDate.subtract(1, 'day'))
    else if (viewType === 'weekly') setSelectedWeek(selectedWeek.subtract(1, 'week'))
    else setSelectedMonth(selectedMonth.subtract(1, 'month'))
  }
  const handleNext = () => {
    if (viewType === 'daily') setSelectedDate(selectedDate.add(1, 'day'))
    else if (viewType === 'weekly') setSelectedWeek(selectedWeek.add(1, 'week'))
    else setSelectedMonth(selectedMonth.add(1, 'month'))
  }
  const navigationTitle = () => {
    if (viewType === 'daily') return selectedDate.format('dddd, MMM D, YYYY')
    if (viewType === 'weekly')
      return `${selectedWeek.startOf('week').format('MMM D')} - ${selectedWeek.endOf('week').format('MMM D, YYYY')}`
    return selectedMonth.format('MMMM YYYY')
  }

  const renderSlot = (slot) => {
    const startTime = slot.slotStartTime || slot.startTime || '--:--'
    const endTime = slot.slotEndTime || slot.endTime || '--:--'
    const clinicBrand = slot.clinicBrandName || slot.clinicBrand?.name || 'Clinic'
    const clinicLocation = slot.clinicLocationName || slot.clinicLocation?.name || ''
    const roomTitle = slot.chamberRoomTitle || slot.chamberRoom?.title || 'Room'
    return (
      <Box
        key={slot.id || `${slot.slotDate}-${startTime}`}
        sx={{
          p: 1.5,
          m: 1,
          borderRadius: '12px',
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(5, 150, 105, 0.08) 100%)',
          border: '1px solid rgba(16, 185, 129, 0.2)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <AccessTimeIcon sx={{ fontSize: '1rem', color: 'success.main' }} />
          <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.dark' }}>
            {startTime} - {endTime}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <BusinessIcon sx={{ fontSize: '0.875rem', color: 'text.secondary' }} />
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {clinicBrand}
          </Typography>
        </Box>
        {clinicLocation && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <LocationOnIcon sx={{ fontSize: '0.875rem', color: 'text.secondary' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {clinicLocation}
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MeetingRoomIcon sx={{ fontSize: '0.875rem', color: 'text.secondary' }} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {roomTitle}
          </Typography>
        </Box>
      </Box>
    )
  }

  const renderDayCard = (day) => {
    const dateStr = day.format('YYYY-MM-DD')
    const isToday = day.isSame(dayjs(), 'day')
    const isPast = day.isBefore(dayjs(), 'day')
    const slots = slotsByDate.get(dateStr) || []
    const presencePairs = actualPresenceData.presenceByDate.get(dateStr) || []
    const hasEmrData = (emrLogsByDate.get(dateStr) || []).length > 0
    const actualDayMins = presencePairs.reduce((s, p) => s + p.durationMins, 0)
    const rosteredDayMins = rosterMinutesByDate.get(dateStr) || 0
    const dayPercentage = rosteredDayMins > 0 ? Math.round((actualDayMins / rosteredDayMins) * 100) : 0
    const incompleteForDay = actualPresenceData.incompleteCheckIns.filter((c) => c.date === dateStr)

    return (
      <Paper
        key={dateStr}
        sx={{
          borderRadius: '16px',
          overflow: 'hidden',
          border: isToday ? '2px solid rgba(99, 102, 241, 0.3)' : '1px solid rgba(0, 0, 0, 0.06)',
          background: isToday
            ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.06) 0%, rgba(139, 92, 246, 0.06) 100%)'
            : isPast
            ? 'rgba(248, 250, 252, 0.9)'
            : 'rgba(255, 255, 255, 0.95)',
          opacity: isPast ? 0.8 : 1,
        }}
      >
        <Box
          sx={{
            p: 1.5,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: isToday
              ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
              : 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
          }}
        >
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {day.format('dddd')}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {day.format('MMM D, YYYY')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {isToday && (
              <Chip
                label="Today"
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600, fontSize: '0.7rem' }}
              />
            )}
            {hasEmrData && (
              <Tooltip title="View Timeline">
                <IconButton size="small" onClick={() => handleOpenTimeline(day)} sx={{ color: 'rgba(255,255,255,0.85)' }}>
                  <TimelineIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {(actualDayMins > 0 || incompleteForDay.length > 0) && (
          <Box
            sx={{
              mx: 1,
              mt: 1,
              p: 1,
              borderRadius: '8px',
              background: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid rgba(16, 185, 129, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LoginIcon sx={{ fontSize: '0.875rem', color: '#10b981' }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#059669' }}>
                Actual: {actualDayMins > 0 ? fmtDuration(actualDayMins) : '0h'}
                {rosteredDayMins > 0 && (
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{
                      ml: 0.5,
                      fontWeight: 700,
                      color: dayPercentage >= 90 ? '#10b981' : dayPercentage >= 70 ? '#d97706' : '#ef4444',
                    }}
                  >
                    ({dayPercentage}%)
                  </Typography>
                )}
              </Typography>
            </Box>
            {incompleteForDay.length > 0 && (
              <Tooltip title={`${incompleteForDay.length} incomplete check-in(s)`}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <WarningAmberIcon sx={{ fontSize: '0.875rem', color: '#f59e0b' }} />
                  <Typography variant="caption" sx={{ color: '#d97706' }}>
                    {incompleteForDay.length}
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </Box>
        )}

        <Box sx={{ minHeight: 90 }}>
          {loading ? (
            <Box sx={{ p: 2 }}>
              <Skeleton variant="rounded" height={56} />
            </Box>
          ) : slots.length > 0 ? (
            slots.map((slot) => renderSlot(slot))
          ) : (
            <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary', fontStyle: 'italic', fontSize: '0.875rem' }}>
              {isPast ? 'No assignments' : 'No scheduled slots'}
            </Box>
          )}
        </Box>
      </Paper>
    )
  }

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', p: { xs: 1.5, md: 2 } }}>
      {/* Controls */}
      <Paper
        sx={{
          p: 2,
          mb: 2,
          borderRadius: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          background: 'rgba(255, 255, 255, 0.95)',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            My Roster
          </Typography>
          <ToggleButtonGroup
            value={scope}
            exclusive
            size="small"
            onChange={(e, v) => v && setScope(v)}
          >
            <ToggleButton value="this" sx={{ textTransform: 'none', fontWeight: 600 }}>
              <LocationOnIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
              {clinicName || 'This clinic'}
            </ToggleButton>
            <ToggleButton value="all" sx={{ textTransform: 'none', fontWeight: 600 }}>
              <BusinessIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
              All clinics
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <ToggleButtonGroup value={viewType} exclusive size="small" onChange={(e, v) => v && setViewType(v)}>
          <ToggleButton value="daily" sx={{ textTransform: 'none', fontWeight: 600 }}>
            <ViewDayIcon sx={{ mr: 0.5, fontSize: '1.1rem' }} /> Daily
          </ToggleButton>
          <ToggleButton value="weekly" sx={{ textTransform: 'none', fontWeight: 600 }}>
            <ViewWeekIcon sx={{ mr: 0.5, fontSize: '1.1rem' }} /> Weekly
          </ToggleButton>
          <ToggleButton value="monthly" sx={{ textTransform: 'none', fontWeight: 600 }}>
            <CalendarViewMonthIcon sx={{ mr: 0.5, fontSize: '1.1rem' }} /> Monthly
          </ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handlePrevious} sx={{ bgcolor: 'rgba(0,0,0,0.05)' }}>
            <ChevronLeftIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarTodayIcon sx={{ color: 'text.secondary', fontSize: '1.1rem' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, minWidth: 200, textAlign: 'center' }}>
              {navigationTitle()}
            </Typography>
          </Box>
          <IconButton onClick={handleNext} sx={{ bgcolor: 'rgba(0,0,0,0.05)' }}>
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Paper>

      {/* Content */}
      {viewType === 'daily' ? (
        <Box sx={{ maxWidth: 480, mx: 'auto' }}>{renderDayCard(selectedDate)}</Box>
      ) : viewType === 'weekly' ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(7, 1fr)' },
            gap: 2,
          }}
        >
          {weekDays.map((day) => renderDayCard(day))}
        </Box>
      ) : (
        <Paper sx={{ borderRadius: '16px', overflow: 'hidden' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', py: 1.5 }}>
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
              <Typography key={d} sx={{ textAlign: 'center', fontWeight: 700, color: '#fff', fontSize: '0.8rem' }}>
                {d}
              </Typography>
            ))}
          </Box>
          {monthCalendar.map((week, wi) => (
            <Box key={wi} sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderTop: wi > 0 ? '1px solid rgba(0,0,0,0.08)' : 'none' }}>
              {week.map((day) => {
                const dateStr = day.format('YYYY-MM-DD')
                const isCurrentMonth = day.month() === selectedMonth.month()
                const isToday = day.isSame(dayjs(), 'day')
                const slots = slotsByDate.get(dateStr) || []
                const hasEmrData = (emrLogsByDate.get(dateStr) || []).length > 0
                const presencePairs = actualPresenceData.presenceByDate.get(dateStr) || []
                const actualDayMins = presencePairs.reduce((s, p) => s + p.durationMins, 0)
                return (
                  <Box
                    key={dateStr}
                    onClick={() => hasEmrData && handleOpenTimeline(day)}
                    sx={{
                      minHeight: 92,
                      p: 1,
                      borderRight: '1px solid rgba(0,0,0,0.05)',
                      opacity: isCurrentMonth ? 1 : 0.4,
                      background: isToday ? 'rgba(99, 102, 241, 0.08)' : 'transparent',
                      cursor: hasEmrData ? 'pointer' : 'default',
                    }}
                  >
                    <Typography sx={{ fontWeight: isToday ? 700 : 500, fontSize: '0.8rem', color: isToday ? '#6366f1' : 'text.primary', mb: 0.5 }}>
                      {day.format('D')}
                    </Typography>
                    {actualDayMins > 0 && (
                      <Box sx={{ px: 0.5, py: 0.25, mb: 0.5, borderRadius: '4px', background: 'rgba(16,185,129,0.1)', fontSize: '0.55rem', fontWeight: 600, color: '#059669', display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                        <LoginIcon sx={{ fontSize: '0.55rem' }} />
                        {fmtDuration(actualDayMins)}
                      </Box>
                    )}
                    {slots.slice(0, 2).map((slot, idx) => (
                      <Box key={idx} sx={{ px: 0.75, py: 0.25, mb: 0.25, borderRadius: '6px', background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)', fontSize: '0.62rem', fontWeight: 600, color: '#6366f1', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {slot.slotStartTime || '--:--'} - {slot.slotEndTime || '--:--'}
                      </Box>
                    ))}
                    {slots.length > 2 && (
                      <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary', fontStyle: 'italic' }}>
                        +{slots.length - 2} more
                      </Typography>
                    )}
                  </Box>
                )
              })}
            </Box>
          ))}
        </Paper>
      )}

      {/* Summary */}
      {!loading && (
        <Paper sx={{ mt: 3, p: 3, borderRadius: '16px', background: 'rgba(255,255,255,0.92)' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
            Summary
          </Typography>
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#6366f1' }}>
                {rosterData.length}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Total Slots
              </Typography>
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#8b5cf6' }}>
                {fmtDuration(totalAllottedMinutes)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Allotted Duration
              </Typography>
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#10b981' }}>
                  {fmtDuration(actualPresenceData.totalActualMinutes)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: overallPercentage >= 90 ? '#10b981' : overallPercentage >= 70 ? '#f59e0b' : '#ef4444' }}
                >
                  ({overallPercentage}%)
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Actual vs Roster
              </Typography>
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#f59e0b' }}>
                {actualPresenceData.incompleteCheckIns.length}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Incomplete Check-ins
              </Typography>
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#64748b' }}>
                {new Set(rosterData.map((s) => s.clinicBrandName || s.clinicBrand?.name).filter(Boolean)).size}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Clinics
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Timeline Drawer */}
      <Drawer
        anchor="right"
        open={timelineDialogOpen}
        onClose={() => {
          setTimelineDialogOpen(false)
          setShowAllLogs(false)
        }}
        PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}
      >
        <Box sx={{ p: 2, background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TimelineIcon />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Timeline - {timelineDate?.format('MMM D, YYYY')}
            </Typography>
          </Box>
          <IconButton onClick={() => { setTimelineDialogOpen(false); setShowAllLogs(false) }} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(0,0,0,0.08)', background: 'rgba(99,102,241,0.05)' }}>
          <FormControlLabel
            control={<Switch checked={showAllLogs} onChange={(e) => setShowAllLogs(e.target.checked)} size="small" />}
            label={<Typography variant="body2" sx={{ fontWeight: 500 }}>Show all check-in logs</Typography>}
          />
        </Box>
        <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
          {timelineDate && (
            <Box sx={{ position: 'relative', pl: 4 }}>
              <Box sx={{ position: 'absolute', left: 12, top: 0, bottom: 0, width: 2, background: 'linear-gradient(to bottom, #e2e8f0, #94a3b8)', borderRadius: 1 }} />
              {getTimelineEvents(timelineDate).map((event, idx) => (
                <Box key={idx} sx={{ position: 'relative', mb: 2, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box sx={{ position: 'absolute', left: -28, width: 24, height: 24, borderRadius: '50%', bgcolor: event.color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                    {event.icon === 'login' && <LoginIcon sx={{ fontSize: '0.875rem' }} />}
                    {event.icon === 'logout' && <LogoutIcon sx={{ fontSize: '0.875rem' }} />}
                    {event.icon === 'warning' && <WarningAmberIcon sx={{ fontSize: '0.875rem' }} />}
                    {event.icon === 'event' && <EventIcon sx={{ fontSize: '0.875rem' }} />}
                    {event.icon === 'refresh' && <RefreshIcon sx={{ fontSize: '0.875rem' }} />}
                    {event.icon === 'approved' && <CheckCircleIcon sx={{ fontSize: '0.875rem' }} />}
                    {event.icon === 'denied' && <CancelIcon sx={{ fontSize: '0.875rem' }} />}
                    {event.icon === 'request' && <PendingActionsIcon sx={{ fontSize: '0.875rem' }} />}
                    {event.icon === 'peek' && <VisibilityIcon sx={{ fontSize: '0.875rem' }} />}
                    {event.icon === 'idle' && <HourglassEmptyIcon sx={{ fontSize: '0.875rem' }} />}
                    {(event.icon === 'default' || event.icon === 'audit') && <AssignmentIndIcon sx={{ fontSize: '0.875rem' }} />}
                  </Box>
                  <Box sx={{ flex: 1, p: 1.5, borderRadius: '10px', background: `${event.color}10`, border: `1px solid ${event.color}30` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: event.color }}>
                        {event.title}
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>
                        {event.time}
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {event.subtitle}
                    </Typography>
                  </Box>
                </Box>
              ))}
              {getTimelineEvents(timelineDate).length === 0 && (
                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', py: 2 }}>
                  No events for this day
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Drawer>
    </Box>
  )
}
